<script lang="ts">
  // Eine rollende Fläche, in der nichts angeschnitten wird (Tim 16.10.2026:
  // „es sollte nix abgeschnitten werden. Wenn ein Element nicht mehr
  // vollständig hinpasst, dann besser mit den drei Punkten anzeigen, dass
  // darunter noch was kommt").
  //
  // Elemente tragen data-zeile. Passt das unterste nicht mehr ganz hinein,
  // deckt eine Leiste „⋯ N weitere" den Rest ab -- ein Klick blättert
  // weiter. Beim Rollen rasten die Elemente oben ein (scroll-snap), damit
  // auch dort keine halbe Zeile steht. Die Leiste liegt ÜBER der Fläche,
  // statt sie zu verkleinern: eine Höhe, die von der eigenen Messung
  // abhinge, schaukelte sich auf (die Fläche schrumpft, misst weniger,
  // schrumpft wieder).
  import type { Snippet } from "svelte";
  import { t } from "../i18n.svelte";

  let { children, class: klasse = "" }: { children: Snippet; class?: string } = $props();

  let roll = $state<HTMLDivElement | undefined>(undefined);
  let decke = $state(0);
  let weitere = $state(0);
  let auslauf = $state(0);
  let bisUnten = 0;
  const PLATZ = 22; // so viel braucht die Leiste mindestens

  function lagenVon(r: HTMLDivElement): { oben: number; unten: number }[] {
    const basis = r.getBoundingClientRect().top - r.scrollTop;
    return [...r.querySelectorAll<HTMLElement>("[data-zeile]")].map((el) => {
      const b = el.getBoundingClientRect();
      return { oben: b.top - basis, unten: b.bottom - basis };
    });
  }

  // Oben angeschnitten (Tim 16.10.: der Verbrauch stand ein paar Pixel zu
  // hoch, ganz unten gerollt -- unten passte alles, oben fehlte die halbe
  // erste Zeile): das Element ganz zeigen, wenn mehr als die Hälfte zu
  // sehen ist, sonst überspringen. Nach einer Größenänderung und wenn das
  // Rollen ruht -- nie mitten im Rollen, das kämpfte gegen die Hand.
  function rasteOben(): void {
    const r = roll;
    if (!r) return;
    const oben = r.scrollTop;
    const l = lagenVon(r).find((x) => x.oben < oben - 0.5 && x.unten > oben + 0.5);
    if (!l) return;
    const ziel = oben - l.oben <= l.unten - oben ? l.oben : l.unten;
    r.scrollTop = ziel;
    // Ganz unten geht es nicht weiter: dann eben das Element ganz zeigen.
    if (Math.abs(r.scrollTop - ziel) > 1) r.scrollTop = l.oben;
  }

  function messe(): void {
    const r = roll;
    if (!r) return;
    const h = r.clientHeight;
    const oben = r.scrollTop;
    const lagen = lagenVon(r);
    // Auslauf hinter dem Inhalt, wie VS Code hinter der letzten Zeile: ganz
    // unten soll oben eine GANZE Zeile stehen UND die letzte ganz zu sehen
    // sein. Ohne ihn endete das Rollen mitten in einer Zeile -- oben
    // eingerastet, passte die letzte nicht mehr, und man sah sie nie.
    const inhaltEnde = r.scrollHeight - auslauf;
    const letzteUnten = lagen.length ? Math.max(...lagen.map((l) => l.unten)) : 0;
    if (inhaltEnde > h + 0.5 && lagen.length) {
      const start = Math.min(...lagen.filter((l) => letzteUnten - l.oben <= h).map((l) => l.oben));
      auslauf = Number.isFinite(start) ? Math.max(0, Math.ceil(start + h - inhaltEnde)) : 0;
    } else {
      auslauf = 0;
    }
    // Passt alles Übrige ganz hinein, braucht es keine Leiste.
    if (!lagen.length || lagen.every((l) => l.unten <= oben + h + 0.5)) {
      decke = weitere = 0;
      return;
    }
    // Sonst: das letzte Element, das samt Platz für die Leiste ganz passt.
    let letztes = oben;
    for (const l of lagen) if (l.oben >= oben - 0.5 && l.unten <= oben + h - PLATZ) letztes = Math.max(letztes, l.unten);
    weitere = lagen.filter((l) => l.oben >= letztes - 0.5).length;
    decke = weitere ? oben + h - letztes : 0;
    bisUnten = letztes - oben;
  }

  let bild = 0;
  function bald(): void {
    cancelAnimationFrame(bild);
    bild = requestAnimationFrame(messe);
  }
  // Größe oder Inhalt geändert: erst einrasten, dann messen.
  function neu(): void {
    cancelAnimationFrame(bild);
    bild = requestAnimationFrame(() => {
      rasteOben();
      messe();
    });
  }
  let ruhe: ReturnType<typeof setTimeout> | undefined;
  function gerollt(): void {
    bald();
    clearTimeout(ruhe);
    ruhe = setTimeout(rasteOben, 150);
  }

  $effect(() => {
    const r = roll;
    if (!r) return;
    const ro = new ResizeObserver(neu);
    ro.observe(r);
    if (r.firstElementChild) ro.observe(r.firstElementChild);
    // Neue Zeilen (Chat kommt dazu, Datei wird committet) ändern die Höhe
    // des Inhalts nicht immer -- dann sagt es der Baum selbst.
    const mo = new MutationObserver(neu);
    mo.observe(r, { childList: true, subtree: true });
    neu();
    return () => {
      ro.disconnect();
      mo.disconnect();
      cancelAnimationFrame(bild);
      clearTimeout(ruhe);
    };
  });

  function weiter(): void {
    roll?.scrollBy({ top: Math.max(bisUnten, 24), behavior: "smooth" });
  }
</script>

<div class="ganze {klasse}">
  <div class="roll" bind:this={roll} onscroll={gerollt}>
    {@render children()}
    {#if auslauf > 0}<div aria-hidden="true" style:height="{auslauf}px"></div>{/if}
  </div>
  {#if decke > 0}
    <button type="button" class="mehr t-meta" style:height="{decke}px" onclick={weiter} title={t("shell.weitere_titel", "Weiter nach unten")}>
      <span class="punkte" aria-hidden="true">⋯</span>{t("shell.weitere", "{n} weitere").replace("{n}", String(weitere))}
    </button>
  {/if}
</div>

<style>
  .ganze {
    position: relative;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
  .roll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    scroll-snap-type: y proximity;
  }
  .roll :global([data-zeile]) {
    scroll-snap-align: start;
  }
  .mehr {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0 0.75rem 0 1.25rem;
    background: var(--shell-kasten, var(--bg));
    color: var(--fg-subtle);
    text-align: left;
  }
  .mehr:hover {
    color: var(--fg);
  }
  .punkte {
    width: 0.8rem;
    text-align: center;
    font-weight: 700;
    letter-spacing: 0.05em;
  }
</style>
